import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AuthUser } from "../../models/AuthUser";
import authRestClient from "../../services/clients/AuthRestClient";
import UserStorage from "../../storages/UserStorage";

const Login = () => {

    const loadingIconStyle = {
        width: "32px"
    }

    const navigate = useNavigate();

    const [email, setEmail]             = useState<string>("");
    const [mailSuccess, setMailSuccess] = useState<boolean>(false);
    const [code, setCode]               = useState<string>("");

    /* Error State for storing error messages */
    const [error, setError] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        /* Get current logged in user */
        let user: AuthUser | null = UserStorage.getCurrentUser();
        if (user != null && new Date(user.refreshTokenExpiryTime) > new Date())
        {
            /* User is already looged in */
            navigate("/me/claims");
        }
    }, []);

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint to 
     * login the current user. 
     * @param e
     */
    async function handleInitSubmit(e: React.FormEvent<HTMLFormElement>): Promise<boolean>
    {
        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();

        let success: boolean = false;
        setIsLoading(true);

        authRestClient.post("/auth/guest", { Email: email })
            .then(response =>
            {
                /* Check if request is successfully made */
                if (response.status === 200)
                {
                    success = true
                    setMailSuccess(true);

                    /* Cleanup error state */
                    setError("");
                }
                setIsLoading(false);
            })
            .catch((err: AxiosError) =>
            {
                setError("An unknown error has occurred");
                setIsLoading(false);
            })

        return success;
    }

    /**
     * Method that handles form submit. Send form data via axios instance to api endpoint to 
     * check the code and login the user accordingly.
     * @param e
     */
    async function handleCodeSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void>
    {
        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();
        setIsLoading(true);

        authRestClient.post<AuthUser>("/auth/guest/login", { confirmationcode: code, Email: email })
            .then(response => {
                /* Check for valid response and fetch response data 
                 * And store authenticated user in storage
                 */
                if (response.status === 200)
                {
                    UserStorage.setUser(response.data);
                    setIsLoading(false);

                    /* After successfully login redirect to user overview */
                    navigate("/me/claims");
                }
            })
            .catch((err: AxiosError) =>
            {
                if (err.response!.status === 401) { setError("The code you entered has expired or is invalid.") }
                else { setError("An unknown error has occurred"); }

                setCode(""); /* Cleanup previously entered code */
                setIsLoading(false);
            });
    }

    /**
     * Functions renders HTML Form Element for entering an posting email information and start login process for guest users.
     * @returns JSX Element
     */
    function RenderMailInput(): JSX.Element {
        return (
            <form onSubmit={handleInitSubmit}>
                <div className="row">
                    <div className="form-group col-12">
                        <label htmlFor="email" className="fw-bold">E-Mail</label>
                        <input type="email" id="email" name="email" className="form-control" onChange={e => setEmail(e.target.value)} disabled={isLoading} />
                    </div>
                    <div className="form-group col-12">
                        {isLoading
                            ? <button type="button" className="btn btn-outline-primary w-100" disabled={true}><img src="/Loading.gif" style={loadingIconStyle} /></button>
                            : <button type="submit" className="btn btn-primary w-100">Confirm and continue</button>
                        }
                    </div>
                </div>
            </form>
        )
    }

    /**
     * Functions renders HTML Form Element for entering an received Guest login code.
     * @returns JSX Element
     */
    function RenderCodeInut(): JSX.Element {
        return (
            <form onSubmit={handleCodeSubmit} className="p-3">
                <div className="row">
                    <h4>Confirm your E-Mail</h4>
                    <p>We have sent a confirmation code to {email}.
                       Not the correct email address? <a href="#">Change it here</a></p>
                    <hr />
                </div>
                <div className="row">
                    <div className="col-12 form-group">
                        <label htmlFor="code" className="fw-bold">Confirmation Code</label>
                        <input id="code" name="code" className="form-control" onChange={e => setCode(e.target.value)} />
                    </div>
                    <hr />
                    <div className="col-12">
                        <p>Didn't get an email? Check your spam folder or have it resent to you.</p>
                    </div>
                    <div className="col-12">
                        <button type="submit" className="btn btn-primary w-100">Send</button>
                    </div>
                </div>
            </form>
        )
    }

    /* JSX Element that represents the current page */
    return (
        <main className="container mb-5">

            <div className="row text-center">
                <div className="col-12">
                    <img src="/brand.png" alt="brand" className="brand" />
                </div>
            </div>

            <div className="row">
                <div className="col-12 col-md-7 mx-auto">
                    <h4>Welcome to Academy of European Law</h4>
                    <p>Please Log in to view and complete your requests by providing the email address you used to create travel applications.</p>
                </div>
            </div>

            {(error !== "") && (
                <div className="row">
                    <div className="col-12 col-md-7 mx-auto">
                        <div className="alert alert-danger fw-bold">
                            {error}
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-12 col-md-7 mx-auto">

                    <div className="card">
                        <div className="card-body">
                            {mailSuccess ? RenderCodeInut() : RenderMailInput()}
                        </div>
                    </div>

                </div>
            </div>

        </main>
    )
}

export default Login;